import React, { useEffect, useLayoutEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { setTabTitle } from "@utils";
import { PartnerRegistrationStore } from "@state/stores/membership/partner-registration";
import {
	usePartnerRegistrationStore,
	PartnerRegistrationStoreContext,
} from "@state/context/membership/registration-types/PartnerRegistrationStoreContext";
import {
	RegistrationError,
	RegistrationSuccess,
} from "@features/membership/mobile/registration-types/registration";
import { PartnerRegistrationTabs } from "@features/membership/mobile/registration-types/partner-registration";
import {
	PartnerRegistrationAccountDetails,
	PartnerRegistrationContactDetails,
} from "./";
import { OnBoardingSlider } from "@features/membership/mobile/registration-types/shared";
import { Loader } from "@components/loader/mobile/";
import { RegistrationFormData } from "@data-types/membership/registration";
import { useRootAppStore } from "@hooks";
import { MobilePlatform } from "@state/stores/types";

type FormFillData = {
	firstName: string | null;
	lastName: string | null;
	dob: {
		day: string;
		month: string;
		year: string;
	} | null;
	mobilePhone: string | null;
};

export default observer(function PartnerRegistrationPage() {
	const rootStore = useRootAppStore<MobilePlatform>();
	const partnerRegistrationStore = useMemo(
		() => new PartnerRegistrationStore(rootStore),
		[]
	);
	const { t } = useTranslation("registration");

	const { onInitialize, onDispose } = partnerRegistrationStore;

	useLayoutEffect(() => {
		rootStore.pageStore.setMinimalPageHeader();
	}, []);

	useEffect(() => {
		setTabTitle(t("REGISTRATION_TITLE"));

		const params = new URLSearchParams(window.location.search);

		if (params.get("shopId")) {
			onInitialize();
		} else {
			partnerRegistrationStore.isRegistrationSuccessful = false;
		}

		return () => onDispose();
	}, []);

	return (
		<PartnerRegistrationStoreContext.Provider
			value={partnerRegistrationStore}
		>
			<PartnerRegistrationPageContent />
		</PartnerRegistrationStoreContext.Provider>
	);
});

const PartnerRegistrationPageContent = observer(
	function PartnerRegistrationPageContent() {
		const methods = useForm<RegistrationFormData>({
			mode: "onChange",
			defaultValues: {
				languageId: "en-US",
			},
		});
		const { handleSubmit, setValue } = methods;

		const { t } = useTranslation("registration");

		const { isRegistrationSuccessful, currentStep, register, isLoading } =
			usePartnerRegistrationStore();

		const params = new URLSearchParams(window.location.search);

		const shopId = params.get("shopId") as string;

		useEffect(() => {
			const formData: FormFillData = {
				firstName: null,
				lastName: null,
				dob: null,
				mobilePhone: null,
			};
			if (params.get("firstname")) {
				formData.firstName = params.get("firstname");
			}
			if (params.get("lastname")) {
				formData.lastName = params.get("lastname");
			}
			if (params.get("dob")) {
				const dobArray = params.get("dob")?.split("-");
				if (dobArray?.length == 3) {
					formData.dob = {
						day: dobArray[2],
						month: dobArray[1],
						year: dobArray[0],
					};
				}
			}
			if (params.get("phonenumber")) {
				//phone number needs to contain +,
				//if we receive + symbol in params it is recognized as whitespace
				formData.mobilePhone = "+" + params.get("phonenumber");
			}

			for (const key of Object.keys(formData) as Array<
				keyof FormFillData
			>) {
				const value = formData[key];
				if (value != undefined) {
					if (key === "dob" && typeof value !== "string") {
						setValue("day", value.day);
						setValue("month", value.month);
						setValue("year", value.year);
					} else {
						setValue(key, value as keyof RegistrationFormData);
					}
				}
			}
		}, []);

		if (isRegistrationSuccessful === true) {
			return <RegistrationSuccess />;
		}

		if (isRegistrationSuccessful === false) {
			return <RegistrationError />;
		}
		return (
			<div className="container">
				<div className="section--membership">
					<OnBoardingSlider />
					<Loader isLoading={isLoading} />

					<FormProvider {...methods}>
						<form
							className="at-regDetails section--membership__form form--grid"
							onSubmit={handleSubmit(
								(formData: RegistrationFormData) =>
									register(formData, shopId)
							)}
						>
							<div className="section--membership__header">
								<div className="section--membership__title">
									{currentStep === 1
										? t("ACCOUNT_DETAILS_TAB")
										: t("CONTACT_DETAILS_TAB")}
								</div>
								<PartnerRegistrationTabs />
							</div>

							<Steps />
						</form>
					</FormProvider>
				</div>
			</div>
		);
	}
);

const Steps = observer(function Steps() {
	const { currentStep } = usePartnerRegistrationStore();

	switch (currentStep) {
		case 1:
			return <PartnerRegistrationAccountDetails />;

		case 2:
			return <PartnerRegistrationContactDetails />;

		default:
			return null;
	}
});
